import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { formatLocationData as jsFormatLocationData } from 'components/Locations/utils';
import * as client from '../api/queriesClient';
import {
  LocationAddress,
  LocationsData,
  LocationsState,
  TransportMode,
} from './queryModels';

function formatLocationDataTyped(input: any): LocationsData {
  const result = jsFormatLocationData(input) as any;

  if (!result || typeof result !== 'object') {
    //throw new Error('formatLocationData returned invalid result');
    //TODO: getting an error here seems to wipe out all the origins on the backend!!! :O
  }

  return {
    ...result,
    UserQueryId: input?.UserQueryId,
    Selected: result?.Selected ?? null,
  } as LocationsData;
}

export const getLocations = createAsyncThunk(
  'locations/getLocations',
  async () => {
    const response = await client.getQueries();
    if (!response.data || response.data.length === 0)
      throw new Error('no data');
    return response.data;
  },
);

export const setDestination = createAsyncThunk(
  'locations/setDestination',
  async (payload: { data: LocationAddress; cookie: string }, { getState }) => {
    const state = getState() as RootState;
    let userQueryId = state.locations.data.UserQueryId;

    if (!userQueryId) {
      const initResponse = await client.createQuery();
      userQueryId = initResponse.data;
    }

    const response = await client.addDestination(userQueryId!, {
      State: payload.data.State,
      PostCode: payload.data.PostCode,
      Latitude: payload.data.Latitude,
      Longitude: payload.data.Longitude,
      UnitNumber: payload.data.UnitNumber,
      StreetNumber: payload.data.StreetNumber,
      StreetName: payload.data.StreetName,
      SuburbName: payload.data.SuburbName,
    });
    return response.data;
  },
);

export const addOrigin = createAsyncThunk(
  'locations/addOrigin',
  async (payload: { data: LocationAddress; cookie: string }, { getState }) => {
    const state = getState() as RootState;
    let userQueryId = state.locations.data.UserQueryId;

    if (!userQueryId) {
      const initResponse = await client.createQuery();
      userQueryId = initResponse.data;
    }

    const response = await client.addOrigin(userQueryId!, {
      State: payload.data.State,
      PostCode: payload.data.PostCode,
      Latitude: payload.data.Latitude,
      Longitude: payload.data.Longitude,
      UnitNumber: payload.data.UnitNumber,
      StreetNumber: payload.data.StreetNumber,
      StreetName: payload.data.StreetName,
      SuburbName: payload.data.SuburbName,
    });
    return response.data;
  },
);

export const deleteLocation = createAsyncThunk(
  'locations/deleteLocation',
  async (
    payload: {
      id: string | null;
      locationType: 'All' | 'Destination' | 'Origins';
      cookie: string;
    },
    { getState },
  ) => {
    const state = getState() as RootState;
    const userQueryId = state.locations.data.UserQueryId;
    if (!userQueryId) return state.locations.data;

    if (payload.locationType === 'Destination') {
      const response = await client.deleteDestination(userQueryId);
      return response.data;
    }

    if (payload.locationType === 'Origins' && payload.id) {
      const response = await client.deleteOrigin(userQueryId, payload.id);
      return response.data;
    }

    // If 'All', maybe we delete the whole query or just clear it?
    // User request: "DELETE /query/{id}/destination/{id} to delete destination"
    // and "DELETE /query/{id}/origin/{id} to delete origin"
    // For 'All' we might want to delete the query entity itself, but existing deleteLocation
    // seems to just return a model. Let's stick to destination/origin for now.

    return state.locations.data;
  },
);

const initialState: LocationsState = {
  data: {
    Destination: null,
    Minutes: 30,
    TransportMode: TransportMode.Car,
    Origins: [],
    Selected: null,
    calculateTime: true,
  } as LocationsData,
  status: 'idle',
  error: null,
  calculateTime: true,
  isInitialLoad: true,
  isAddingOrigin: false,
};

const queriesSlicer = createSlice({
  name: 'queries',
  initialState,
  reducers: {
    clearLocationsOnLogOut: (state) => ({
      ...state,
      data: initialState.data,
      isInitialLoad: true,
    }),
    setCalculateTime: (state, action: PayloadAction<boolean>) => ({
      ...state,
      calculateTime: action.payload,
    }),
    setSelected: (state, action: PayloadAction<string | null>) => ({
      ...state,
      data: {
        ...state.data,
        Selected: action.payload,
      },
    }),
    updateOriginsDistance: (
      state,
      action: PayloadAction<{ duration: { value: number } }[]>,
    ) => ({
      ...state,
      data: {
        ...state.data,
        Origins: state.data.Origins.map((origin, i) => ({
          ...origin,
          Minutes: action.payload[i]?.duration.value, // ?? origin.Minutes,
        })),
      },
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLocations.pending, (state) => ({
        ...state,
        status: state.isInitialLoad ? 'pending' : 'busy',
      }))
      .addCase(getLocations.fulfilled, (state, action) => ({
        ...state,
        status: 'succeeded',
        data: formatLocationDataTyped(action.payload[0]),
        error: null,
        isInitialLoad: false,
      }))
      .addCase(getLocations.rejected, (state) => ({
        ...state,
        status: 'rejected',
        error: 'no data found',
        isInitialLoad: false,
      }))
      .addCase(setDestination.pending, (state) => ({
        ...state,
        status: 'busy',
      }))
      .addCase(setDestination.fulfilled, (state, action) => ({
        ...state,
        status: 'succeeded',
        data: formatLocationDataTyped(action.payload),
        error: null,
        calculateTime: true,
      }))
      .addCase(setDestination.rejected, (state) => ({
        ...state,
        status: 'rejected',
        error: 'error set Destination',
      }))
      .addCase(addOrigin.pending, (state) => ({
        ...state,
        status: 'busy',
        isAddingOrigin: true,
      }))
      .addCase(addOrigin.fulfilled, (state, action) => ({
        ...state,
        status: 'succeeded',
        isAddingOrigin: false,
        data: formatLocationDataTyped(action.payload),
        error: null,
        calculateTime: true,
      }))
      .addCase(addOrigin.rejected, (state) => ({
        ...state,
        status: 'rejected',
        isAddingOrigin: false,
        error: 'error add origin',
      }))
      .addCase(deleteLocation.pending, (state) => ({
        ...state,
        status: 'busy',
      }))
      .addCase(deleteLocation.fulfilled, (state, action) => ({
        ...state,
        status: 'succeeded',
        data: formatLocationDataTyped(action.payload),
        error: null,
        calculateTime: true,
      }))
      .addCase(deleteLocation.rejected, (state) => ({
        ...state,
        status: 'rejected',
        error: 'error deleting',
      }))
      .addCase(setTravelMode.pending, (state) => ({
        ...state,
        status: 'busy',
      }))
      .addCase(setTravelMode.fulfilled, (state, action) => ({
        ...state,
        status: 'succeeded',
        data: formatLocationDataTyped(action.payload),
        error: null,
        calculateTime: true,
      }))
      .addCase(setTravelMode.rejected, (state) => ({
        ...state,
        status: 'rejected',
        error: 'error setting travel mode',
      }))
      .addCase(setTravelMaxTime.pending, (state) => ({
        ...state,
        status: 'busy',
      }))
      .addCase(setTravelMaxTime.fulfilled, (state, action) => ({
        ...state,
        status: 'succeeded',
        data: formatLocationDataTyped(action.payload),
        error: null,
        calculateTime: true,
      }))
      .addCase(setTravelMaxTime.rejected, (state) => ({
        ...state,
        status: 'rejected',
        error: 'error setting max time',
      }));
  },
});

export const {
  setCalculateTime,
  updateOriginsDistance,
  clearLocationsOnLogOut,
  setSelected,
} = queriesSlicer.actions;

export default queriesSlicer.reducer;

export const setTravelMode = createAsyncThunk(
  'locations/setTravelMode',
  async (payload: { mode: TransportMode }, { getState }) => {
    const state = getState() as RootState;
    let userQueryId = state.locations.data.UserQueryId;
    if (!userQueryId) {
      const initResponse = await client.createQuery();
      userQueryId = initResponse.data;
    }
    const response = await client.updateQuerySettings(userQueryId!, {
      Minutes: state.locations.data.Minutes,
      TransportMode: payload.mode,
    });
    return response.data;
  },
);

export const setTravelMaxTime = createAsyncThunk(
  'locations/setTravelMaxTime',
  async (payload: { minutes: number }, { getState }) => {
    const state = getState() as RootState;
    let userQueryId = state.locations.data.UserQueryId;
    if (!userQueryId) {
      const initResponse = await client.createQuery();
      userQueryId = initResponse.data;
    }
    const response = await client.updateQuerySettings(userQueryId!, {
      Minutes: payload.minutes,
      TransportMode: state.locations.data.TransportMode,
    });
    return response.data;
  },
);
